import Bugsnag from '@bugsnag/js';
import { useQuery } from '@vue/apollo-composable';
import { computed, watchEffect } from 'vue';

import { EntityType } from '@/__generated__/gateway/graphql';
import { MoneyV2 } from '@/__generated__/storefront/graphql.ts';
import {
  Metafield,
  PatternProduct,
  PatternProductsQueryResult,
  PatternProductsQueryVariables,
  PriceRanges,
  Variant
} from '@/shared/composables/patternProducts/patternProducts.types';
import { contextualError } from '@/shared/utils/error.ts';
import { shopifyIdToGid } from '@/shared/utils/shopify/id-formatter.ts';

import {
  patternProductVariantsQuery,
  patternVariantsAndItsMetafield
} from './patternProductsStorefront.queries.ts';

export const usePatternProducts = (
  patternId: string,
  patternProductsList?: PatternProduct[] | null
) => {
  const { result: variantsResult } = useQuery(
    patternVariantsAndItsMetafield,
    { patternId: shopifyIdToGid(patternId, 'Product') },
    { clientId: 'storefront' }
  );

  const patternVariants = computed(
    () => variantsResult?.value?.product?.variants?.nodes || []
  );

  const productIds = computed(() => {
    if (!patternProductsList) return [];
    return Array.from(
      new Set(
        patternProductsList
          .filter((pp) => pp?.product_id)
          .map((pp) => pp.product_id)
      )
    );
  });

  const incompletePatternProducts = computed(() => {
    if (productIds.value.length <= 0) return false;
    if (!hydratedProductList.value || hydratedProductList.value.length === 0) {
      //all the products are missing
      return productIds.value.length;
    }

    const productsWithNoVariant =
      patternProductsList?.filter((pp) => pp.product_id && !pp.variant_id) ||
      [];

    if (productsWithNoVariant.length > 0) return productsWithNoVariant.length;

    return productIds.value.length - hydratedProductList.value.length;
  });

  const {
    result: resultPatternProducts,
    loading: patternProductsLoading,
    error: patternProductsError
  } = useQuery<PatternProductsQueryResult, PatternProductsQueryVariables>(
    patternProductVariantsQuery,
    {
      ids: productIds.value,
      country: window?.Shopify?.country
    },
    {
      clientId: 'storefront'
    }
  );

  const hydratedProductList = computed(
    () => resultPatternProducts.value?.nodes?.filter((a) => a) ?? []
  );

  watchEffect(() => {
    if (!Bugsnag.isStarted()) {
      return;
    }

    if (incompletePatternProducts.value) {
      const incompletePatternProductsError = contextualError(
        'usePatternProducts',
        'Incomplete pattern products, some products are null',
        {
          data: patternProductsList
        }
      );

      if (incompletePatternProductsError) {
        Bugsnag.notify(incompletePatternProductsError);
      }
    }

    if (patternProductsError.value) {
      const patternProductsContextualError = contextualError(
        'usePatternProducts',
        patternProductsError.value,
        {
          data: patternProductsList
        }
      );

      if (patternProductsContextualError) {
        Bugsnag.notify(patternProductsContextualError);
      }
    }
  });

  const getProductSubtitle = (metafields: Metafield[]) => {
    const parsedMetafields = metafields.reduce<Record<string, string>>(
      (metaFieldsObject, metafield) => {
        if (metafield) {
          metaFieldsObject[metafield.key] = metafield.value;
        }
        return metaFieldsObject;
      },
      {}
    );

    const isPattern = parsedMetafields['entity_type'] === EntityType.Pattern;

    const tag = isPattern
      ? parsedMetafields['pattern_kind']
      : parsedMetafields['yarn_weight_category'];

    const subtitle = isPattern
      ? parsedMetafields['designer']
      : parsedMetafields['subtitle'];

    return tag || subtitle;
  };

  const getPrices = (variant: Variant, priceRanges: PriceRanges) => {
    if (variant) {
      return {
        price: variant.price,
        compareAtPrice: variant.compareAtPrice
      };
    }

    return {
      price: priceRanges.price.minVariantPrice,
      compareAtPrice: priceRanges.compareAtPrice.minVariantPrice
    };
  };

  const getDiscountPercentage = (price: MoneyV2, compareAtPrice: MoneyV2) => {
    const oldPrice = parseFloat(compareAtPrice?.amount);
    if (oldPrice && oldPrice > 0) {
      return Math.floor(
        ((oldPrice - parseFloat(price.amount)) * 100) / oldPrice
      );
    }

    return 0;
  };

  return {
    hydratedProductList,
    patternProductsLoading,
    incompletePatternProducts,
    patternVariants,
    getProductSubtitle,
    getPrices,
    getDiscountPercentage
  };
};
